import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { 
  Controller, 
  useFormContext,
  // Removed unused Controller types to prevent potential import errors if types differ
} from 'react-hook-form'; 
// Use the combined type if possible, or adjust based on actual form context type
import { PresetCreateRequest, TradeSetCreateRequest, PresetType, Preset, TradeSetPresetLink } from '../../../types/preset'; 
import { useSymbols } from '../../../hooks/useSymbols'; // Corrected path assumption
import { usePresetNameValidation } from '../../../hooks/usePresetNameValidation';
import { presetService } from '../../../services/presetService';

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Title = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const Description = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0.5rem 0 0 0;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  padding: 0.625rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  width: 100%;
  color: #111827;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const Select = styled.select`
  padding: 0.625rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  width: 100%;
  color: #111827;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
`;

const ErrorText = styled.p`
  color: #dc2626;
  font-size: 0.75rem;
  margin: 0.25rem 0 0 0;
`;

const ValidationText = styled.p<{ $isError?: boolean; $isChecking?: boolean }>`
  font-size: 0.75rem;
  margin: 0.25rem 0 0 0;
  color: ${({ $isError, $isChecking }) => {
    if ($isChecking) return '#6b7280';
    if ($isError) return '#dc2626';
    return '#10b981';
  }};
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const LoadingSpinner = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(107, 114, 128, 0.3);
  border-radius: 50%;
  border-top-color: #6b7280;
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const DeleteButton = styled.button`
  background-color: #dc2626;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #b91c1c;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.2);
  }
`;

// NEW: TradeSet Preset Linking Styles
const PresetLinkingSection = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1rem;
  background-color: #f9fafb;
`;

const PresetLinkingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const SectionLabel = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
`;

const ToggleButton = styled.button<{ $expanded: boolean }>`
  background: none;
  border: none;
  color: #3b82f6;
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #eff6ff;
  }
`;

const PresetList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background-color: white;
`;

const PresetItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f9fafb;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const CheckboxInput = styled.input.attrs({ type: 'checkbox' })`
  margin-right: 0.5rem;
  cursor: pointer;
`;

const PresetItemContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const PresetName = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
`;

const PresetDescription = styled.span`
  font-size: 0.75rem;
  color: #6b7280;
`;

const OrderInput = styled.input.attrs({ type: 'number' })`
  width: 50px;
  padding: 0.25rem;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  text-align: center;
`;

const LoadingText = styled.div`
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
  padding: 1rem;
`;

const EmptyText = styled.div`
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
  padding: 1rem;
  font-style: italic;
`;

interface BasicInfoProps {
  presetType: PresetType;
  editingPresetId?: string; // Add editing preset ID for name validation
  onDeletePreset?: (presetId: string) => void; // Callback for delete action
}

// Define a type that encompasses fields used here for both preset and tradeset
type BasicInfoFormData = Pick<PresetCreateRequest, 'name' | 'description' | 'symbol'> 
                        & Pick<TradeSetCreateRequest, 'name' | 'description' | 'symbol' | 'presets'>;

const BasicInfo: React.FC<BasicInfoProps> = ({ presetType, editingPresetId, onDeletePreset }) => {
  const { control, formState: { errors }, watch, setError, clearErrors, setValue } = useFormContext<BasicInfoFormData>(); // Use the specific type
  const { symbols, isLoading: symbolsLoading } = useSymbols(); // Fetch symbols
  
  // Watch the name field for real-time validation and symbol for preset filtering
  const watchedName = watch('name');
  const watchedSymbol = watch('symbol');
  
  // State for TradeSet preset linking
  const [availablePresets, setAvailablePresets] = useState<Preset[]>([]);
  const [loadingPresets, setLoadingPresets] = useState(false);
  const [linkedPresets, setLinkedPresets] = useState<Map<string, number>>(new Map());
  const [showPresetLinking, setShowPresetLinking] = useState(false);

  // Use the preset name validation hook
  const {
    isValid: isNameValid,
    error: nameValidationError,
    isChecking: isCheckingName,
    checkNameAvailability
  } = usePresetNameValidation(watchedName || '', editingPresetId);

  // Trigger name validation when the name changes
  useEffect(() => {
    if (watchedName) {
      checkNameAvailability(watchedName);
    }
  }, [watchedName, checkNameAvailability]);

  // Update form errors based on name validation
  useEffect(() => {
    if (nameValidationError) {
      setError('name', { 
        type: 'manual', 
        message: nameValidationError 
      });
    } else if (isNameValid && watchedName) {
      clearErrors('name');
    }
  }, [nameValidationError, isNameValid, watchedName, setError, clearErrors]);

  // Fetch available presets when symbol changes (for TradeSets)
  useEffect(() => {
    if (presetType === 'tradeset' && watchedSymbol && showPresetLinking) {
      const fetchPresets = async () => {
        setLoadingPresets(true);
        try {
          const response = await presetService.getPresets(1, 100, undefined, watchedSymbol);
          setAvailablePresets(response.items);
        } catch (error) {
          console.error('Error fetching presets:', error);
          setAvailablePresets([]);
        } finally {
          setLoadingPresets(false);
        }
      };
      
      fetchPresets();
    }
  }, [presetType, watchedSymbol, showPresetLinking]);

  // Update form with linked presets
  useEffect(() => {
    if (presetType === 'tradeset') {
      const presetLinks: TradeSetPresetLink[] = Array.from(linkedPresets.entries())
        .map(([preset_id, order]) => ({ preset_id, order }));
      setValue('presets', presetLinks);
    }
  }, [linkedPresets, presetType, setValue]);

  const handleDeleteClick = () => {
    if (editingPresetId && onDeletePreset) {
      const presetName = watchedName || 'this preset';
      if (window.confirm(`Are you sure you want to delete "${presetName}"? This action cannot be undone.`)) {
        onDeletePreset(editingPresetId);
      }
    }
  };

  const handlePresetToggle = (presetId: string) => {
    setLinkedPresets(prev => {
      const newLinkedPresets = new Map(prev);
      
      if (newLinkedPresets.has(presetId)) {
        newLinkedPresets.delete(presetId);
      } else {
        const maxOrder = prev.size > 0 ? Math.max(...Array.from(prev.values())) : 0;
        newLinkedPresets.set(presetId, maxOrder + 1);
      }
      
      return newLinkedPresets;
    });
  };

  const handleOrderChange = (presetId: string, newOrder: number) => {
    setLinkedPresets(prev => {
      const newLinkedPresets = new Map(prev);
      if (newLinkedPresets.has(presetId)) {
        newLinkedPresets.set(presetId, newOrder);
      }
      return newLinkedPresets;
    });
  };
  
  return (
    <StepContainer>
      <HeaderSection>
        <HeaderContent>
          <Title>Basic Information</Title>
          <Description>
            Enter the basic details for your {presetType}. {presetType === 'preset' ? 'This defines an executable trading action.' : 'This defines a container to group related preSets for a specific symbol.'}
          </Description>
        </HeaderContent>
        {editingPresetId && onDeletePreset && (
          <DeleteButton onClick={handleDeleteClick}>
            Delete PreSet
          </DeleteButton>
        )}
      </HeaderSection>
      
      <FormGroup>
        <InputGroup>
          <Label htmlFor="name">Name</Label>
          <Controller
            name="name"
            control={control}
            rules={{ required: `${presetType === 'preset' ? 'Preset' : 'TradeSet'} name is required` }} // Example required rule
            render={({ field, fieldState }) => (
              <>
                <Input 
                  id="name"
                  placeholder={`Enter a descriptive name for the ${presetType}`}
                  {...field}
                />
                {fieldState.error && <ErrorText>{fieldState.error.message}</ErrorText>}
                {/* Real-time validation feedback */}
                {watchedName && !fieldState.error && (
                  <ValidationText $isError={!isNameValid} $isChecking={isCheckingName}>
                    {isCheckingName && <LoadingSpinner />}
                    {isCheckingName && 'Checking availability...'}
                    {!isCheckingName && nameValidationError && nameValidationError}
                    {!isCheckingName && !nameValidationError && isNameValid && watchedName.length > 1 && '✓ Name is available'}
                  </ValidationText>
                )}
              </>
            )}
          />
        </InputGroup>
        
        <InputGroup>
          <Label htmlFor="description">Description (optional)</Label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <Input 
                id="description"
                placeholder={`Add details about this ${presetType}`}
                {...field}
                value={field.value ?? ''} // Handle null value
              />
            )}
          />
          {/* No explicit error display needed if not required */}
        </InputGroup>
        
        {/* Symbol is now required for both */}
        <InputGroup>
          <Label htmlFor="symbol">Symbol</Label>
          <Controller
            name="symbol"
            control={control}
            rules={{ required: 'Symbol is required' }}
            render={({ field, fieldState }) => (
              <>
                <select 
                  id="symbol"
                  {...field}
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    fontSize: '1rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">
                    {symbolsLoading ? 'Loading symbols...' : 'Select Symbol'}
                  </option>
                  {!symbolsLoading && symbols.map((symbol) => (
                    <option key={symbol} value={symbol}>
                      {symbol}
                    </option>
                  ))}
                </select>
                {fieldState.error && <ErrorText>{fieldState.error.message}</ErrorText>}
              </>
            )}
          />
        </InputGroup>

        {/* TradeSet Preset Linking Section */}
        {presetType === 'tradeset' && watchedSymbol && (
          <PresetLinkingSection>
            <PresetLinkingHeader>
              <SectionLabel>
                Linked Presets ({linkedPresets.size}) 
                <span style={{ fontWeight: 'normal', fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.5rem' }}>
                  (Optional - you can add presets later)
                </span>
              </SectionLabel>
              <ToggleButton 
                type="button"
                $expanded={showPresetLinking}
                onClick={() => setShowPresetLinking(!showPresetLinking)}
              >
                {showPresetLinking ? 'Hide' : 'Link Presets'}
              </ToggleButton>
            </PresetLinkingHeader>
            
            {showPresetLinking && (
              <>
                {loadingPresets ? (
                  <LoadingText>Loading available presets for {watchedSymbol}...</LoadingText>
                ) : availablePresets.length === 0 ? (
                  <EmptyText>
                    No presets found for {watchedSymbol}. 
                    Create some presets first, then you can link them to this TradeSet.
                  </EmptyText>
                ) : (
                  <PresetList>
                    {availablePresets.map((preset) => {
                      const isLinked = linkedPresets.has(preset.id);
                      const order = linkedPresets.get(preset.id) || 1;
                      
                      return (
                        <PresetItem key={preset.id}>
                          <CheckboxInput
                            checked={isLinked}
                            onChange={() => handlePresetToggle(preset.id)}
                          />
                          <PresetItemContent>
                            <PresetName>{preset.name}</PresetName>
                            <PresetDescription>
                              {preset.order_type} • {preset.side} • {preset.description || 'No description'}
                            </PresetDescription>
                          </PresetItemContent>
                          {isLinked && (
                            <OrderInput
                              value={order}
                              onChange={(e) => handleOrderChange(preset.id, parseInt(e.target.value, 10) || 1)}
                              min={1}
                              title="Execution order"
                            />
                          )}
                        </PresetItem>
                      );
                    })}
                  </PresetList>
                )}
              </>
            )}
          </PresetLinkingSection>
        )}
      </FormGroup>
    </StepContainer>
  );
};

export default BasicInfo; 